import type { LocaleCode } from '~/constants/i18n';
import { localeSyncHook } from '~/utils/i18n/hydration';
import { SSR_COOKIE_LOCALE_STATE_KEY } from '~/utils/i18n/ssrLocale';

// Keeps the client locale in step with the server render, then with the visitor's preference.
//
// 1. Before hydration: if the server rendered an opted-out route in the visitor's cookie locale
//    (plugins/i18n-ssr-cookie-locale.server.ts), adopt that exact locale now — the client must
//    hydrate with the same strings/attributes as the server HTML. @nuxtjs/i18n alone would start
//    unprefixed routes in the default locale.
// 2. After hydration (`app:suspense:resolve` for server-rendered pages — `app:mounted` fires
//    before async page hydration finishes; `app:mounted` for CSR-only loads): apply the full preference (profile > cookie > browser).
//    The global route middleware (middleware/i18n-locale.global.ts) does nothing during the
//    initial (pre-hydration) navigation of a server-rendered page — no redirect, no
//    `applyLocale` — so this sync is what redirects (e.g. `/masterclass` → `/he/masterclass` for
//    a `he` cookie) or switches a page whose server render didn't match the preference. When
//    step 1 already aligned the locale, `applyLocale` is a no-op here (no string flash). On CSR-only
//    loads both may run; `applyLocale`/`navigateTo` are idempotent, so that is harmless.
export default defineNuxtPlugin({
  name: 'thrivestate:i18n-locale',
  dependsOn: ['i18n:plugin:route-locale-detect'],
  async setup(nuxtApp) {
    const router = useRouter();
    const authStore = useAuthStore();

    const ssrLocale = useState<LocaleCode | null>(SSR_COOKIE_LOCALE_STATE_KEY, () => null);
    const serverLocale = ssrLocale.value;
    // useNuxtApp(), not the setup argument: the latter types `$i18n` as unknown in plugins.
    const { $i18n } = useNuxtApp();
    if (nuxtApp.payload.serverRendered && serverLocale && $i18n.locale.value !== serverLocale) {
      try {
        await $i18n.loadLocaleMessages(serverLocale);
        $i18n.locale.value = serverLocale;
      } catch (error) {
        useErrorHandler().catchErrors(error);
      }
    }

    const sync = async () => {
      const { resolveAction, applyAction } = useLocaleSwitch();
      const action = resolveAction(router.currentRoute.value);
      if (action.type === 'redirect') {
        await navigateTo(action.to, { replace: true });
        return;
      }
      await applyAction(action);
    };

    nuxtApp.hooks.hookOnce(localeSyncHook(nuxtApp.payload.serverRendered), sync);
    watch(
      () => authStore.user?.languageIso,
      (next, prev) => {
        // `switchLocale` (composables/useLocaleSwitch.ts) already updates `languageIso` and
        // navigates/applies the new locale itself — without this guard, this watcher fires
        // mid-switch and calls `sync()` on top of it, double-navigating.
        if (next !== prev && !isSwitchingLocale()) sync();
      },
    );
  },
});
